import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  AlertCircle,
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  CreditCard,
  Infinity as InfinityIcon,
  Minus,
  Plus,
  Wallet,
} from 'lucide-react';
import { db, functions } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { isIosShell, isNativeShell } from '../lib/nativeBridge';
import { Badge, Spinner, cx } from './ui';

interface Pack {
  credits: number;
  price: number;
  label: string;
}

interface LedgerRow {
  id: string;
  delta: number;
  balanceAfter: number;
  reason: string;
  kind: string;
  note: string;
  at: string | null;
}

interface PaymentRow {
  id: string;
  amount: number;
  currency: string;
  credits: number;
  method: string;
  paidAt: string | null;
}

interface Summary {
  ok: true;
  balance: number;
  comped: boolean;
  labName: string;
  packs: Record<string, Pack>;
  ledger: LedgerRow[];
  payments: PaymentRow[];
}

const callSummary = httpsCallable<void, Summary>(functions, 'billingSummary');
const callTopUp = httpsCallable<{ packId: string; from?: 'app' }, { ok: true; url: string; orderId: string }>(
  functions,
  'createTopUp',
);

/** Where a billing question actually reaches a person. */
const CONTACT = 'noreply.blisx@gmail.com';

const REASON_LABEL: Record<string, string> = {
  extraction: 'Instrument read',
  purchase: 'Credits purchased',
  grant: 'Credits added',
  refund: 'Credits removed',
};

function when(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Credits, what they were spent on, and what was paid for them.
 *
 * The balance here is live rather than fetched once. A purchase completes
 * through a HitPay webhook, which lands seconds after the browser comes back
 * from the payment page — a number loaded on mount would still read the old
 * total, and the natural response to that is to pay again. A Firestore listener
 * on the account means the figure moves on its own the moment the webhook
 * credits it.
 */
export default function Billing() {
  const { user } = useAuth();
  // Inside the App Store build, credits are only read, never sold: no prices,
  // no top-up, no payment history, no pointer to where to buy. Selling them in
  // the app would need Apple's in-app purchase (guideline 3.1.1); showing a
  // balance bought elsewhere rests on 3.1.3(f), which holds only while
  // the app shows no purchasing and no call to action.
  const iosApp = isIosShell();
  // Inside either app, HitPay opens in the phone's browser rather than here.
  const inApp = isNativeShell();
  const [openedCheckout, setOpenedCheckout] = useState(false);
  const [params] = useSearchParams();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [liveBalance, setLiveBalance] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** True when we have just come back from paying. */
  const returningFromPayment = Boolean(params.get('order'));

  const load = useCallback(async () => {
    try {
      const res = await callSummary();
      setSummary(res.data);
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not load your billing details.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The live half: the balance, straight off the account document.
  useEffect(() => {
    if (!user) return undefined;
    return onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        const credits = Number(snap.data()?.credits ?? 0);
        setLiveBalance(credits);
        // A change while sitting here almost always means a payment landed, so
        // pull the history down again to match.
        setSummary((prev) => (prev && prev.balance !== credits ? { ...prev, balance: credits } : prev));
      },
      () => setLiveBalance(null),
    );
  }, [user]);

  // Coming back from the payment page, the webhook may still be in flight.
  useEffect(() => {
    if (!returningFromPayment) return undefined;
    const t = window.setTimeout(() => void load(), 4000);
    return () => window.clearTimeout(t);
  }, [returningFromPayment, load]);

  async function buy(packId: string) {
    setBusy(packId);
    setError(null);
    try {
      const res = await callTopUp(inApp ? { packId, from: 'app' } : { packId });
      // HitPay's own page, on their domain. Nothing here handles card details.
      // In the app the shell opens it in the browser and this page stays put,
      // so say where it went instead of spinning forever.
      window.location.href = res.data.url;
      if (inApp) {
        setOpenedCheckout(true);
        setBusy(null);
      }
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not start that payment.');
      setBusy(null);
    }
  }

  const balance = liveBalance ?? summary?.balance ?? 0;
  const comped = summary?.comped === true;

  // The way back shows from the first frame, not only once the summary loads:
  // in the phone app that can take a few seconds.
  const back = (
    <Link
      to="/"
      className="mb-1 inline-flex items-center gap-1 text-sm text-ink-400 hover:text-ink-100 pointer-coarse:min-h-11"
    >
      <ArrowLeft size={14} />
      Projects
    </Link>
  );

  if (!summary && !error) {
    return (
      <div className="mx-auto max-w-2xl">
        {back}
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-5">
        {back}
        <h1 className="text-xl font-semibold">{iosApp ? 'Credits' : 'Credits and billing'}</h1>
        <p className="text-sm text-ink-400">
          A photographed or uploaded sheet costs four credits; a dictated reading costs one. Typing
          values in and exporting the spreadsheet are always free.
        </p>
      </div>

      {error && (
        <p className="mb-4 flex gap-2 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </p>
      )}

      {!iosApp && returningFromPayment && (
        <p className="mb-4 flex gap-2 rounded-lg border border-sky-800 bg-sky-950 p-3 text-sm text-sky-200">
          <Clock size={16} className="mt-0.5 shrink-0" />
          <span>
            Thanks — your payment is being confirmed. The balance updates by itself the moment it
            clears, usually within a few seconds. A receipt is on its way to your email.
          </span>
        </p>
      )}

      {openedCheckout && (
        <p className="mb-4 flex gap-2 rounded-lg border border-sky-800 bg-sky-950 p-3 text-sm text-sky-200">
          <Clock size={16} className="mt-0.5 shrink-0" />
          <span>
            HitPay opened in your browser. Pay there, then come back — the balance below updates by
            itself once the payment clears.
          </span>
        </p>
      )}

      {/* The balance. Live, so a landing payment moves it without a reload. */}
      <div className="mb-6 rounded-xl border border-ink-700 bg-ink-800/40 p-5">
        {comped ? (
          <>
            <p className="field-label mb-1">Your lab</p>
            <p className="flex items-center gap-2 text-2xl font-semibold text-emerald-300">
              <InfinityIcon size={24} />
              Unlimited
            </p>
            <p className="mt-1.5 text-sm text-ink-400">
              {summary?.labName || 'Your lab'} is not billed for instrument reads. Nothing here is
              charged and there is nothing to buy.
            </p>
          </>
        ) : (
          <>
            <p className="field-label mb-1">Balance</p>
            <p className="flex items-baseline gap-2">
              <span className="text-3xl font-semibold tabular-nums">{balance}</span>
              <span className="text-sm text-ink-400">
                {balance === 1 ? 'credit' : 'credits'}
              </span>
              {liveBalance !== null && (
                <Badge tone="good" className="ml-1">
                  live
                </Badge>
              )}
            </p>
            <p className="mt-1.5 text-sm text-ink-400">
              {balance === 0
                ? iosApp
                  ? 'Out of credits — instrument reads are paused. Typing values in stays free.'
                  : 'Out of credits — instrument reads are paused until you top up.'
                : `About ${Math.floor(balance / 4)} photos, or ${balance} dictated readings.`}
            </p>
          </>
        )}
      </div>

      {!iosApp && !comped && summary?.packs && (
        <>
          <p className="field-label mb-2">Top up</p>
          <div className="mb-6 grid gap-2 sm:grid-cols-3">
            {Object.entries(summary.packs).map(([id, pack]) => (
              <button
                key={id}
                onClick={() => void buy(id)}
                disabled={busy !== null}
                className={cx(
                  'rounded-xl border border-ink-700 bg-ink-800/40 p-4 text-left transition-colors',
                  'hover:border-accent-500 hover:bg-ink-800',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                )}
              >
                <p className="text-lg font-semibold tabular-nums">{pack.credits}</p>
                <p className="text-xs text-ink-400">credits</p>
                <p className="mt-2 flex items-center gap-1 text-sm font-medium text-accent-400">
                  {busy === id ? <Spinner className="size-3.5" /> : <CreditCard size={14} />}
                  SGD {pack.price}
                </p>
              </button>
            ))}
          </div>
          <p className="mb-2 flex items-center gap-1.5 text-xs text-ink-400">
            <ArrowUpRight size={13} />
            Payment is handled by HitPay on their own page. Card details never reach this app.
          </p>
          <p className="mb-6 text-xs text-ink-400">
            Buying credits is subject to our{' '}
            <Link to="/terms" className="text-accent-400 underline-offset-2 hover:underline">
              Terms
            </Link>
            , which cover what a credit buys and that a failed read is never charged.
          </p>
        </>
      )}

      {/* What was paid. Separate from the ledger because this is the part
          somebody has to put through expenses. */}
      {!iosApp && (summary?.payments?.length ?? 0) > 0 && (
        <>
          <p className="field-label mb-2">Payments</p>
          <ul className="mb-6 space-y-1.5">
            {summary!.payments.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-ink-700 bg-ink-800/40 px-3 py-2"
              >
                <CheckCircle2 size={15} className="shrink-0 text-emerald-400" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm">
                    {p.currency} {p.amount.toFixed(2)} — {p.credits} credits
                  </p>
                  <p className="truncate text-xs text-ink-400">
                    {when(p.paidAt)}
                    {p.method ? ` · ${p.method.replace(/_/g, ' ')}` : ''}
                  </p>
                </div>
                <code className="shrink-0 text-xs text-ink-500">{p.id.slice(0, 8)}</code>
                <a
                  href={
                    `mailto:${CONTACT}?subject=${encodeURIComponent(
                      `BlisxBench payment query — ${p.id}`,
                    )}&body=${encodeURIComponent(
                      `Reference: ${p.id}\nPaid: ${p.currency} ${p.amount.toFixed(2)} for ${p.credits} credits\n\nWhat happened:\n`,
                    )}`
                  }
                  className="shrink-0 text-xs text-ink-400 underline-offset-2 hover:text-ink-100 hover:underline"
                >
                  Query
                </a>
              </li>
            ))}
          </ul>
          <p className="mb-6 -mt-4 text-xs text-ink-400">
            If a payment went through and the credits never arrived, say so — that one is on us and
            gets put right.
          </p>
        </>
      )}

      <p className="field-label mb-2">History</p>
      {(summary?.ledger?.length ?? 0) === 0 ? (
        <p className="rounded-lg border border-dashed border-ink-700 px-4 py-6 text-center text-sm text-ink-400">
          {iosApp
            ? 'Nothing yet. Every read shows up here.'
            : 'Nothing yet. Every read and every top-up shows up here.'}
        </p>
      ) : (
        <ul className="space-y-1">
          {summary!.ledger.map((row) => (
            <li
              key={row.id}
              className="flex items-center gap-2 rounded-md bg-ink-900/60 px-3 py-1.5 text-sm"
            >
              {row.delta > 0 ? (
                <Plus size={13} className="shrink-0 text-emerald-400" />
              ) : (
                <Minus size={13} className="shrink-0 text-ink-400" />
              )}
              <span className="min-w-0 flex-1 truncate">
                {REASON_LABEL[row.reason] ?? row.reason}
                {row.note && <span className="text-ink-400"> · {row.note}</span>}
              </span>
              <span className="shrink-0 text-xs text-ink-400">{when(row.at)}</span>
              <span
                className={cx(
                  'w-12 shrink-0 text-right tabular-nums',
                  row.delta > 0 ? 'text-emerald-300' : 'text-ink-300',
                )}
              >
                {row.delta > 0 ? `+${row.delta}` : row.delta}
              </span>
            </li>
          ))}
        </ul>
      )}

      {!iosApp && (
        <p className="mt-6 flex items-center gap-1.5 text-xs text-ink-400">
          <Wallet size={13} />
          Questions about a charge? Reply to any receipt and it reaches a person.
        </p>
      )}
    </div>
  );
}
